import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Tabs,
  Rate,
  Progress,
  DatePicker,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FormOutlined,
  BarChartOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import { handleExport } from '../utils/exportUtils';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Survey {
  _id: string;
  title: string;
  description?: string;
  surveyType: string;
  questions: Array<{
    questionText: string;
    questionType: string;
    options?: Array<{ text: string; value: string }>;
    required: boolean;
    order: number;
  }>;
  status: string;
  totalResponses: number;
  averageRating: number;
  station: {
    _id: string;
    name: string;
  };
  settings: {
    allowAnonymous: boolean;
    startDate?: string;
    endDate?: string;
  };
}

interface SurveyResponse {
  _id: string;
  responses: Array<{
    questionId: string;
    questionText: string;
    answer: any;
  }>;
  overallRating?: number;
  comments?: string;
  submittedAt: string;
  customer?: {
    firstName: string;
    lastName: string;
  };
}

const Surveys: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [analyticsModalVisible, setAnalyticsModalVisible] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [activeTab, setActiveTab] = useState('surveys');
  const [filters, setFilters] = useState<any>({
    stationId: 'all',
    status: 'all',
    surveyType: 'all',
    search: ''
  });
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (activeTab === 'responses' && selectedSurvey) {
      fetchResponses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSurvey]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.surveyType !== 'all') params.surveyType = filters.surveyType;
      if (filters.search) params.search = filters.search;

      const response = await stationOwnerAPI.getSurveys(params);
      setSurveys(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Anketler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchResponses = async () => {
    if (!selectedSurvey) return;
    setResponsesLoading(true);
    try {
      const response = await stationOwnerAPI.getSurveyResponses(selectedSurvey._id);
      setResponses(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Yanıtlar yüklenemedi');
    } finally {
      setResponsesLoading(false);
    }
  };

  const fetchAnalytics = async (survey: Survey) => {
    try {
      const response = await stationOwnerAPI.getSurveyAnalytics(survey._id);
      setAnalytics(response);
      setSelectedSurvey(survey);
      setAnalyticsModalVisible(true);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Analiz yüklenemedi');
    }
  };

  const handleAddSurvey = () => {
    setSelectedSurvey(null);
    form.resetFields();
    form.setFieldsValue({
      surveyType: 'customer_satisfaction',
      status: 'draft',
      questions: [{
        questionText: '',
        questionType: 'rating',
        required: false,
        order: 0
      }],
      settings: {
        allowAnonymous: false,
        showProgress: true,
        randomizeQuestions: false
      }
    });
    setModalVisible(true);
  };

  const handleEditSurvey = (survey: Survey) => {
    setSelectedSurvey(survey);
    form.setFieldsValue({
      ...survey,
      stationId: survey.station._id,
      'settings.startDate': survey.settings.startDate ? dayjs(survey.settings.startDate) : undefined,
      'settings.endDate': survey.settings.endDate ? dayjs(survey.settings.endDate) : undefined
    });
    setModalVisible(true);
  };

  const handleSubmitSurvey = async (values: any) => {
    try {
      const data = {
        stationId: values.stationId,
        title: values.title,
        description: values.description,
        surveyType: values.surveyType,
        questions: values.questions.map((q: any, index: number) => ({
          questionText: q.questionText,
          questionType: q.questionType,
          options: q.options || [],
          required: q.required || false,
          order: index
        })),
        status: values.status,
        settings: {
          allowAnonymous: values.settings?.allowAnonymous || false,
          showProgress: values.settings?.showProgress !== false,
          randomizeQuestions: values.settings?.randomizeQuestions || false,
          limitResponses: values.settings?.limitResponses || null,
          startDate: values.settings?.startDate?.toISOString(),
          endDate: values.settings?.endDate?.toISOString()
        }
      };

      if (selectedSurvey) {
        await stationOwnerAPI.updateSurvey(selectedSurvey._id, data);
        message.success('Anket güncellendi');
      } else {
        await stationOwnerAPI.createSurvey(data);
        message.success('Anket oluşturuldu');
      }
      setModalVisible(false);
      form.resetFields();
      setSelectedSurvey(null);
      fetchSurveys();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDeleteSurvey = async (id: string) => {
    try {
      await stationOwnerAPI.deleteSurvey(id);
      message.success('Anket silindi');
      fetchSurveys();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleViewResponses = (survey: Survey) => {
    setSelectedSurvey(survey);
    setActiveTab('responses');
  };

  const columns = [
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station'
    },
    {
      title: 'Tip',
      dataIndex: 'surveyType',
      key: 'surveyType',
      render: (type: string) => {
        const colors: any = {
          customer_satisfaction: 'blue',
          service_quality: 'green',
          product_feedback: 'orange',
          general: 'default',
          custom: 'purple'
        };
        const labels: any = {
          customer_satisfaction: 'Müşteri Memnuniyeti',
          service_quality: 'Hizmet Kalitesi',
          product_feedback: 'Ürün Geri Bildirimi',
          general: 'Genel',
          custom: 'Özel'
        };
        return <Tag color={colors[type]}>{labels[type] || type}</Tag>;
      }
    },
    {
      title: 'Soru Sayısı',
      key: 'questionCount',
      render: (record: Survey) => record.questions?.length || 0
    },
    {
      title: 'Yanıt Sayısı',
      dataIndex: 'totalResponses',
      key: 'totalResponses',
      render: (count: number) => count || 0
    },
    {
      title: 'Ortalama Puan',
      dataIndex: 'averageRating',
      key: 'averageRating',
      render: (rating: number) => rating ? <Rate disabled value={rating} /> : '-'
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: any = {
          draft: 'default',
          active: 'success',
          paused: 'warning',
          closed: 'error'
        };
        const labels: any = {
          draft: 'Taslak',
          active: 'Aktif',
          paused: 'Duraklatıldı',
          closed: 'Kapatıldı'
        };
        const icons: any = {
          draft: <FormOutlined />,
          active: <CheckCircleOutlined />,
          paused: <PauseCircleOutlined />,
          closed: <ClockCircleOutlined />
        };
        return (
          <Tag color={colors[status]} icon={icons[status]}>
            {labels[status] || status}
          </Tag>
        );
      }
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Survey) => (
        <Space>
          <Button
            type="link"
            icon={<BarChartOutlined />}
            onClick={() => fetchAnalytics(record)}
            title="Analiz"
          />
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewResponses(record)}
            title="Yanıtlar"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditSurvey(record)}
          />
          <Popconfirm
            title="Bu anketi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteSurvey(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const responseColumns = [
    {
      title: 'Tarih',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      render: (date: string) => dayjs(date).format('DD MMM YYYY HH:mm')
    },
    {
      title: 'Müşteri',
      key: 'customer',
      render: (record: SurveyResponse) => {
        if (record.customer) {
          return `${record.customer.firstName} ${record.customer.lastName}`;
        }
        return 'Anonim';
      }
    },
    {
      title: 'Genel Değerlendirme',
      dataIndex: 'overallRating',
      key: 'overallRating',
      render: (rating: number) => rating ? <Rate disabled value={rating} /> : '-'
    },
    {
      title: 'Yorum',
      dataIndex: 'comments',
      key: 'comments',
      ellipsis: true
    }
  ];

  const totalSurveys = surveys.length;
  const activeSurveys = surveys.filter(s => s.status === 'active').length;
  const totalResponses = surveys.reduce((sum, s) => sum + (s.totalResponses || 0), 0);
  const averageRating = surveys.length > 0 
    ? surveys.reduce((sum, s) => sum + (s.averageRating || 0), 0) / surveys.length 
    : 0;

  const tabItems = [
    {
      key: 'surveys',
      label: 'Anketler',
      children: (
        <>
          {/* Özet İstatistikler */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Anket"
                  value={totalSurveys}
                  prefix={<FormOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Aktif Anket"
                  value={activeSurveys}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Yanıt"
                  value={totalResponses}
                  prefix={<BarChartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Ortalama Puan"
                  value={averageRating}
                  precision={1}
                  prefix={<Rate disabled value={Math.round(averageRating)} />}
                />
              </Card>
            </Col>
          </Row>

          {/* Filtreler */}
          <Card style={{ marginBottom: 24 }} styles={{ body: { padding: isMobile ? '12px' : '16px' } }}>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Ara (başlık, açıklama)"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  allowClear
                  size={isMobile ? 'small' : 'middle'}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  value={filters.stationId}
                  onChange={(value) => setFilters({ ...filters, stationId: value })}
                  style={{ width: '100%' }}
                  placeholder="İstasyon"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Option value="all">Tüm İstasyonlar</Option>
                  {stations.map((station: any) => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  value={filters.status}
                  onChange={(value) => setFilters({ ...filters, status: value })}
                  style={{ width: '100%' }}
                  placeholder="Durum"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Option value="all">Tümü</Option>
                  <Option value="draft">Taslak</Option>
                  <Option value="active">Aktif</Option>
                  <Option value="paused">Duraklatıldı</Option>
                  <Option value="closed">Kapatıldı</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  value={filters.surveyType}
                  onChange={(value) => setFilters({ ...filters, surveyType: value })}
                  style={{ width: '100%' }}
                  placeholder="Anket Tipi"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Option value="all">Tümü</Option>
                  <Option value="customer_satisfaction">Müşteri Memnuniyeti</Option>
                  <Option value="service_quality">Hizmet Kalitesi</Option>
                  <Option value="product_feedback">Ürün Geri Bildirimi</Option>
                  <Option value="general">Genel</Option>
                  <Option value="custom">Özel</Option>
                </Select>
              </Col>
            </Row>
          </Card>

          <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
            <Table
              columns={columns}
              dataSource={surveys}
              rowKey="_id"
              loading={loading}
              pagination={{ 
                pageSize: 10,
                showSizeChanger: false,
                responsive: true
              }}
              scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
              size={isMobile ? 'small' : 'middle'}
            />
          </Card>
        </>
      )
    },
    {
      key: 'responses',
      label: 'Yanıtlar',
      children: selectedSurvey ? (
        <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
          <div style={{ marginBottom: 16 }}>
            <Title level={4}>{selectedSurvey.title}</Title>
            <Button onClick={() => setSelectedSurvey(null)}>Geri</Button>
          </div>
          <Table
            columns={responseColumns}
            dataSource={responses}
            rowKey="_id"
            loading={responsesLoading}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: false,
              responsive: true
            }}
            scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
            size={isMobile ? 'small' : 'middle'}
            expandable={{
              expandedRowRender: (record: SurveyResponse) => (
                <div>
                  {record.responses.map((resp: any, index: number) => (
                    <Card key={index} size="small" style={{ marginBottom: 8 }}>
                      <p><strong>{resp.questionText}</strong></p>
                      <p>{typeof resp.answer === 'object' ? JSON.stringify(resp.answer) : resp.answer}</p>
                    </Card>
                  ))}
                  {record.comments && (
                    <Card size="small">
                      <p><strong>Yorum:</strong> {record.comments}</p>
                    </Card>
                  )}
                </div>
              )
            }}
          />
        </Card>
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Yanıtları görüntülemek için bir anket seçin</p>
          </div>
        </Card>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            <FormOutlined /> Anket Sistemi
          </Title>
        </Col>
        <Col xs={24} md={12}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }} wrap>
            {activeTab === 'surveys' && (
              <>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleAddSurvey} 
                  block={isMobile}
                >
                  Yeni Anket Oluştur
                </Button>
                <Button 
                  icon={<FileExcelOutlined />}
                  onClick={() => handleExport(
                    () => stationOwnerAPI.exportSurveysExcel(),
                    `anketler_${Date.now()}.xlsx`,
                    () => message.success('Excel başarıyla indirildi')
                  )}
                  block={isMobile}
                >
                  Excel İndir
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* Anket Modal */}
      <Modal
        title={selectedSurvey ? 'Anket Düzenle' : 'Yeni Anket Oluştur'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedSurvey(null);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 900}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitSurvey}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="stationId"
                label="İstasyon"
                rules={[{ required: true, message: 'İstasyon seçiniz' }]}
              >
                <Select placeholder="İstasyon seçiniz">
                  {stations.map((station: any) => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="surveyType"
                label="Anket Tipi"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="customer_satisfaction">Müşteri Memnuniyeti</Option>
                  <Option value="service_quality">Hizmet Kalitesi</Option>
                  <Option value="product_feedback">Ürün Geri Bildirimi</Option>
                  <Option value="general">Genel</Option>
                  <Option value="custom">Özel</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="title"
                label="Başlık"
                rules={[{ required: true, message: 'Başlık gereklidir' }]}
              >
                <Input placeholder="Anket başlığı" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="description"
                label="Açıklama"
              >
                <TextArea rows={3} placeholder="Anket açıklaması" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="status"
                label="Durum"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="draft">Taslak</Option>
                  <Option value="active">Aktif</Option>
                  <Option value="paused">Duraklatıldı</Option>
                  <Option value="closed">Kapatıldı</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.List name="questions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} style={{ marginBottom: 16 }} size="small">
                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <Form.Item
                          {...restField}
                          name={[name, 'questionText']}
                          label="Soru"
                          rules={[{ required: true }]}
                        >
                          <Input placeholder="Soru metni" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'questionType']}
                          label="Soru Tipi"
                          rules={[{ required: true }]}
                        >
                          <Select>
                            <Option value="rating">Değerlendirme (1-5)</Option>
                            <Option value="multiple_choice">Çoktan Seçmeli</Option>
                            <Option value="yes_no">Evet/Hayır</Option>
                            <Option value="text">Metin</Option>
                            <Option value="scale">Ölçek (1-10)</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'required']}
                          valuePropName="checked"
                        >
                          <Switch checkedChildren="Zorunlu" unCheckedChildren="Opsiyonel" />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Button type="link" danger onClick={() => remove(name)}>
                          Soruyu Sil
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Soru Ekle
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Card title="Ayarlar" size="small" style={{ marginTop: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['settings', 'allowAnonymous']}
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Anonim izin ver" unCheckedChildren="Anonim yasak" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['settings', 'startDate']}
                  label="Başlangıç Tarihi"
                >
                  <DatePicker style={{ width: '100%' }} showTime />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['settings', 'endDate']}
                  label="Bitiş Tarihi"
                >
                  <DatePicker style={{ width: '100%' }} showTime />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedSurvey ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setSelectedSurvey(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Analiz Modal */}
      <Modal
        title={`Anket Analizi - ${selectedSurvey?.title}`}
        open={analyticsModalVisible}
        onCancel={() => {
          setAnalyticsModalVisible(false);
          setAnalytics(null);
        }}
        footer={null}
        width={isMobile ? '95%' : 1000}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {analytics && (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Toplam Yanıt"
                    value={analytics.totalResponses}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Ortalama Puan"
                    value={analytics.averageRating}
                    precision={2}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Yanıt Oranı"
                    value={analytics.responseRate}
                    precision={1}
                    suffix="%"
                  />
                </Card>
              </Col>
            </Row>

            {/* Genel Değerlendirme Dağılımı */}
            {analytics.byRating && (
              <Card title="Genel Değerlendirme Dağılımı" style={{ marginBottom: 24 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[
                    { name: '1 Yıldız', value: analytics.byRating[1] || 0 },
                    { name: '2 Yıldız', value: analytics.byRating[2] || 0 },
                    { name: '3 Yıldız', value: analytics.byRating[3] || 0 },
                    { name: '4 Yıldız', value: analytics.byRating[4] || 0 },
                    { name: '5 Yıldız', value: analytics.byRating[5] || 0 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#1890ff" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Soru Bazında Analiz */}
            {analytics.byQuestion && Object.keys(analytics.byQuestion).length > 0 && (
              <Card title="Soru Bazında Analiz" style={{ marginBottom: 24 }}>
                {Object.entries(analytics.byQuestion).map(([question, data]: [string, any]) => (
                  <Card key={question} size="small" style={{ marginBottom: 16 }}>
                    <p><strong>{question}</strong></p>
                    {data.average && (
                      <p>Ortalama: {data.average.toFixed(2)}</p>
                    )}
                    {data.distribution && (
                      <div>
                        {Object.entries(data.distribution).map(([key, value]: [string, any]) => (
                          <div key={key} style={{ marginBottom: 8 }}>
                            <span>{key}: </span>
                            <Progress percent={(value / data.total) * 100} showInfo={false} />
                            <span style={{ marginLeft: 8 }}>{value} ({((value / data.total) * 100).toFixed(1)}%)</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Surveys;

